import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Minus, TrendingUp, TrendingDown, ShoppingCart, DollarSign, Edit2, RefreshCw, Search } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';

const SpotBalanceModal = ({ clientId, holdings, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [activeTab, setActiveTab] = useState('manage'); // 'manage', 'trade', 'pnl'
  const [asset, setAsset] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [operation, setOperation] = useState('add');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Trade fields
  const [tradeType, setTradeType] = useState('buy');
  const [tradePrice, setTradePrice] = useState('');
  const [tradeQuantity, setTradeQuantity] = useState('');
  
  // PnL fields
  const [pnlAsset, setPnlAsset] = useState('');
  const [newPnl, setNewPnl] = useState('');
  const [newAvgPrice, setNewAvgPrice] = useState('');
  
  // Market prices
  const [marketPrices, setMarketPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  
  // Search for assets
  const [assetSearch, setAssetSearch] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  
  // Dynamic assets from backend
  const [assets, setAssets] = useState(['BTC', 'ETH', 'USDT', 'BNB', 'SOL', 'XRP', 'ADA', 'DOGE', 'MATIC', 'AVAX']);

  // Filtered assets based on search
  const filteredAssets = useMemo(() => {
    if (!assetSearch) return assets;
    const search = assetSearch.toUpperCase();
    return assets.filter(a => a.toUpperCase().includes(search));
  }, [assets, assetSearch]);

  // Load assets from backend
  useEffect(() => {
    const loadAssets = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trading/symbols?limit=100`);
        const data = await res.json();
        // Handle both array and object with symbols property
        const symbolsArray = data?.symbols || (Array.isArray(data) ? data : []);
        if (symbolsArray.length > 0) {
          // Extract base assets from symbols (BTCUSDT -> BTC)
          const assetList = [...new Set(
            symbolsArray
              .map(s => (s.symbol || s).replace('USDT', '').replace('USD', ''))
              .filter(a => typeof a === 'string' && a.length >= 2 && a.length <= 10 && !a.includes('-') && !a.includes('.'))
          )].slice(0, 100);
          // Add USDT to the list
          if (!assetList.includes('USDT')) {
            assetList.push('USDT');
          }
          if (assetList.length > 0) {
            setAssets(assetList);
          }
        }
      } catch (err) {
        console.error('Failed to load assets:', err);
      }
    };
    loadAssets();
  }, []);

  useEffect(() => {
    fetchMarketPrices();
  }, []);

  // Initialize pnlAsset from holdings when they load
  useEffect(() => {
    if (holdings.length > 0 && !pnlAsset) {
      const firstHolding = holdings[0];
      setPnlAsset(firstHolding.asset || firstHolding.symbol || 'BTC');
    }
  }, [holdings, pnlAsset]);

  // Update PnL fields when pnlAsset changes
  useEffect(() => {
    if (pnlAsset) {
      const holding = getCurrentHolding(pnlAsset);
      setNewPnl(holding.pnl?.toString() || '0');
      setNewAvgPrice(holding.avg_buy_price?.toString() || '0');
    }
  }, [pnlAsset]);

  // Set default price when asset changes in trade tab
  useEffect(() => {
    if (activeTab === 'trade' && marketPrices[asset]) {
      setTradePrice(marketPrices[asset].toString());
    }
  }, [asset, activeTab, marketPrices]);

  const fetchMarketPrices = async () => {
    setLoadingPrices(true);
    try {
      const res = await adminAPI.getMarketPrices();
      setMarketPrices(res.data?.prices || {});
    } catch (err) {
      // Use default prices if API fails
      setMarketPrices({ BTC: 43000, ETH: 2200, BNB: 300, SOL: 100, USDT: 1, XRP: 0.62, ADA: 0.52, DOGE: 0.085, MATIC: 0.85, AVAX: 38 });
    } finally {
      setLoadingPrices(false);
    }
  };

  const getCurrentHolding = (assetName) => {
    return holdings.find(h => h.asset === assetName || h.symbol === assetName) || { amount: 0, quantity: 0, avg_buy_price: 0, pnl: 0 };
  };

  const handleManageSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setLoading(true);
    try {
      await adminAPI.adjustSpotBalance(clientId, { asset, amount: parseFloat(amount), operation, notes });
      onSuccess();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTradeSubmit = async () => {
    if (!tradeQuantity || parseFloat(tradeQuantity) <= 0) return;
    if (!tradePrice || parseFloat(tradePrice) <= 0) return;
    setLoading(true);
    try {
      await adminAPI.spotTrade(clientId, {
        asset,
        type: tradeType,
        quantity: parseFloat(tradeQuantity),
        price: parseFloat(tradePrice),
        notes,
      });
      onSuccess();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePnlSubmit = async () => {
    if (!pnlAsset) {
      toast.validation('Выберите актив');
      return;
    }
    setLoading(true);
    try {
      await adminAPI.updateSpotHolding(clientId, {
        asset: pnlAsset,
        pnl: newPnl ? parseFloat(newPnl) : undefined,
        avg_buy_price: newAvgPrice ? parseFloat(newAvgPrice) : undefined,
      });
      onSuccess();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate trade summary
  const tradeTotalValue = tradePrice && tradeQuantity 
    ? parseFloat(tradePrice) * parseFloat(tradeQuantity) 
    : 0;
  
  // Calculate estimated PnL for sell
  const currentHolding = getCurrentHolding(asset);
  const estimatedPnL = tradeType === 'sell' && tradePrice && tradeQuantity && currentHolding.avg_buy_price
    ? (parseFloat(tradePrice) - currentHolding.avg_buy_price) * parseFloat(tradeQuantity)
    : null;

  const tabs = [
    { id: 'manage', label: 'Начислить/Списать', icon: Plus },
    { id: 'trade', label: 'Купить/Продать', icon: ShoppingCart },
    { id: 'pnl', label: 'PnL/Цена', icon: Edit2 },
  ];

  // Get current holding for PnL tab
  const pnlHolding = getCurrentHolding(pnlAsset);

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={(e) => { setShowAssetDropdown(false); onClose(); }}>
      <div style={{ backgroundColor: COLORS.card, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => { e.stopPropagation(); if (e.target.tagName !== 'INPUT') setShowAssetDropdown(false); }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>Spot управление</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}><X size={20} /></button>
        </div>

        {/* Current Holdings */}
        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: COLORS.background, borderRadius: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Текущие активы</div>
            <button 
              onClick={fetchMarketPrices}
              disabled={loadingPrices}
              style={{ 
                background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px'
              }}
            >
              <RefreshCw size={12} className={loadingPrices ? 'animate-spin' : ''} />
              Обновить цены
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {holdings.length > 0 ? holdings.map((h, i) => {
              const assetName = h.asset || h.symbol;
              const currentPrice = marketPrices[assetName] || h.avg_buy_price || 0;
              const qty = h.amount || h.quantity || 0;
              const currentValue = qty * currentPrice;
              return (
                <div key={i} style={{ padding: '8px 12px', backgroundColor: COLORS.card, borderRadius: '6px', border: `1px solid ${COLORS.border}` }}>
                  <div style={{ color: COLORS.text, fontWeight: '600', fontSize: '13px' }}>{assetName}</div>
                  <div style={{ color: COLORS.success, fontSize: '12px' }}>{qty.toFixed(6)}</div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>≈ ${currentValue.toFixed(2)}</div>
                  {h.pnl !== undefined && <div style={{ color: h.pnl >= 0 ? COLORS.success : COLORS.danger, fontSize: '10px' }}>PnL: ${h.pnl?.toFixed(2)}</div>}
                </div>
              );
            }) : <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Нет активов</div>}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              flex: 1, padding: '10px', backgroundColor: activeTab === tab.id ? COLORS.primary : COLORS.background,
              border: 'none', borderRadius: '8px', color: activeTab === tab.id ? '#000' : COLORS.textSecondary,
              fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}>
              <tab.icon size={14} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Manage Tab */}
        {activeTab === 'manage' && (
          <div>
            <div style={{ marginBottom: '12px', position: 'relative' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Актив</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={assetSearch || asset}
                  onChange={(e) => {
                    setAssetSearch(e.target.value);
                    setShowAssetDropdown(true);
                  }}
                  onFocus={() => setShowAssetDropdown(true)}
                  placeholder="Поиск криптовалюты..."
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    paddingLeft: '36px',
                    backgroundColor: COLORS.background, 
                    border: `1px solid ${COLORS.border}`, 
                    borderRadius: '8px', 
                    color: COLORS.text, 
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
                {showAssetDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: COLORS.card,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    marginTop: '4px',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}>
                    {filteredAssets.length > 0 ? filteredAssets.slice(0, 20).map(a => (
                      <div
                        key={a}
                        onClick={() => {
                          setAsset(a);
                          setAssetSearch('');
                          setShowAssetDropdown(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: `1px solid ${COLORS.border}`,
                          backgroundColor: asset === a ? `${COLORS.primary}20` : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${COLORS.primary}10`}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = asset === a ? `${COLORS.primary}20` : 'transparent'}
                      >
                        <span style={{ color: COLORS.text, fontWeight: asset === a ? '600' : '400' }}>{a}</span>
                        <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                          {(getCurrentHolding(a).amount || getCurrentHolding(a).quantity || 0).toFixed(4)}
                        </span>
                      </div>
                    )) : (
                      <div style={{ padding: '12px', color: COLORS.textSecondary, textAlign: 'center' }}>
                        Не найдено
                      </div>
                    )}
                  </div>
                )}
              </div>
              {asset && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: COLORS.textSecondary }}>
                  Выбрано: <span style={{ color: COLORS.primary, fontWeight: '600' }}>{asset}</span> 
                  (баланс: {(getCurrentHolding(asset).amount || getCurrentHolding(asset).quantity || 0).toFixed(6)})
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {[{ id: 'add', label: 'Начислить', desc: 'Добавить актив', icon: Plus, color: COLORS.success },
                { id: 'subtract', label: 'Списать', desc: 'Убрать актив', icon: Minus, color: COLORS.danger }].map(op => (
                <button key={op.id} onClick={() => setOperation(op.id)} style={{
                  flex: 1, padding: '12px', backgroundColor: operation === op.id ? `${op.color}20` : COLORS.background,
                  border: `2px solid ${operation === op.id ? op.color : COLORS.border}`, borderRadius: '8px',
                  color: operation === op.id ? op.color : COLORS.textSecondary, fontWeight: '600', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <op.icon size={16} /> {op.label}
                  </div>
                  <span style={{ fontSize: '10px', opacity: 0.7 }}>{op.desc}</span>
                </button>
              ))}
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Количество {asset}</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" step="0.000001" style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '16px', fontWeight: '600', boxSizing: 'border-box' }} />
              {amount && marketPrices[asset] && (
                <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginTop: '4px' }}>
                  ≈ ${(parseFloat(amount) * marketPrices[asset]).toFixed(2)} по рыночной цене
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Комментарий</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Причина..." style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} />
            </div>

            <button onClick={handleManageSubmit} disabled={loading || !amount} style={{ width: '100%', padding: '14px', backgroundColor: loading || !amount ? COLORS.border : operation === 'add' ? COLORS.success : COLORS.danger, border: 'none', borderRadius: '8px', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: loading || !amount ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Обработка...' : operation === 'add' ? `Начислить ${amount || 0} ${asset}` : `Списать ${amount || 0} ${asset}`}
            </button>
          </div>
        )}

        {/* Trade Tab */}
        {activeTab === 'trade' && (
          <div>
            <div style={{ marginBottom: '12px', position: 'relative' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Актив</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={assetSearch || asset}
                  onChange={(e) => {
                    setAssetSearch(e.target.value);
                    setShowAssetDropdown(true);
                  }}
                  onFocus={() => setShowAssetDropdown(true)}
                  placeholder="Поиск криптовалюты..."
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    paddingLeft: '36px',
                    backgroundColor: COLORS.background, 
                    border: `1px solid ${COLORS.border}`, 
                    borderRadius: '8px', 
                    color: COLORS.text, 
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
                {showAssetDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: COLORS.card,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    marginTop: '4px',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}>
                    {filteredAssets.filter(a => a !== 'USDT').length > 0 ? filteredAssets.filter(a => a !== 'USDT').slice(0, 20).map(a => (
                      <div
                        key={a}
                        onClick={() => {
                          setAsset(a);
                          setAssetSearch('');
                          setShowAssetDropdown(false);
                          setTradePrice(marketPrices[a]?.toString() || '');
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: `1px solid ${COLORS.border}`,
                          backgroundColor: asset === a ? `${COLORS.primary}20` : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${COLORS.primary}10`}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = asset === a ? `${COLORS.primary}20` : 'transparent'}
                      >
                        <span style={{ color: COLORS.text, fontWeight: asset === a ? '600' : '400' }}>{a}</span>
                        <span style={{ color: COLORS.primary, fontSize: '12px' }}>
                          ${marketPrices[a]?.toLocaleString() || '—'}
                        </span>
                      </div>
                    )) : (
                      <div style={{ padding: '12px', color: COLORS.textSecondary, textAlign: 'center' }}>
                        Не найдено
                      </div>
                    )}
                  </div>
                )}
              </div>
              {asset && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: COLORS.textSecondary }}>
                  Выбрано: <span style={{ color: COLORS.primary, fontWeight: '600' }}>{asset}</span>
                  {marketPrices[asset] && <span> • Цена: ${marketPrices[asset].toLocaleString()}</span>}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {[{ id: 'buy', label: 'Купить', icon: TrendingUp, color: COLORS.success },
                { id: 'sell', label: 'Продать', icon: TrendingDown, color: COLORS.danger }].map(t => (
                <button key={t.id} onClick={() => setTradeType(t.id)} style={{
                  flex: 1, padding: '12px', backgroundColor: tradeType === t.id ? `${t.color}20` : COLORS.background,
                  border: `2px solid ${tradeType === t.id ? t.color : COLORS.border}`, borderRadius: '8px',
                  color: tradeType === t.id ? t.color : COLORS.textSecondary, fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}>
                  <t.icon size={16} /> {t.label}
                </button>
              ))}
            </div>

            {/* Price with market price suggestion */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Цена ($)</label>
                {marketPrices[asset] && (
                  <button
                    onClick={() => setTradePrice(marketPrices[asset].toString())}
                    style={{ 
                      background: 'none', border: 'none', color: COLORS.primary, 
                      cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    Рыночная: ${marketPrices[asset]?.toLocaleString()}
                  </button>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <input 
                  type="number" 
                  value={tradePrice} 
                  onChange={(e) => setTradePrice(e.target.value)} 
                  placeholder={marketPrices[asset]?.toString() || '0'} 
                  style={{ width: '100%', padding: '12px', paddingRight: '80px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} 
                />
                <button
                  onClick={fetchMarketPrices}
                  disabled={loadingPrices}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: COLORS.primary, border: 'none', borderRadius: '4px',
                    padding: '4px 8px', color: '#000', fontSize: '10px', fontWeight: '600', cursor: 'pointer'
                  }}
                >
                  <RefreshCw size={10} className={loadingPrices ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Количество</label>
                {tradeType === 'sell' && (currentHolding.amount || currentHolding.quantity) > 0 && (
                  <button
                    onClick={() => setTradeQuantity((currentHolding.amount || currentHolding.quantity).toString())}
                    style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: '11px' }}
                  >
                    Макс: {(currentHolding.amount || currentHolding.quantity || 0).toFixed(6)}
                  </button>
                )}
              </div>
              <input type="number" value={tradeQuantity} onChange={(e) => setTradeQuantity(e.target.value)} placeholder="0.00" step="0.000001" style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Комментарий</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Примечание к сделке..." style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} />
            </div>

            {/* Trade Summary */}
            {tradePrice && tradeQuantity && (
              <div style={{ padding: '12px', backgroundColor: COLORS.background, borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Сумма сделки:</span>
                  <span style={{ color: COLORS.text, fontSize: '15px', fontWeight: '700' }}>${tradeTotalValue.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{tradeType === 'buy' ? 'Списание с баланса' : 'Зачисление на баланс'}:</span>
                  <span style={{ color: tradeType === 'buy' ? COLORS.danger : COLORS.success, fontSize: '13px', fontWeight: '600' }}>
                    {tradeType === 'buy' ? '-' : '+'}${tradeTotalValue.toFixed(2)}
                  </span>
                </div>
                {estimatedPnL !== null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '6px', borderTop: `1px solid ${COLORS.border}` }}>
                    <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Ожидаемый PnL:</span>
                    <span style={{ color: estimatedPnL >= 0 ? COLORS.success : COLORS.danger, fontSize: '13px', fontWeight: '600' }}>
                      {estimatedPnL >= 0 ? '+' : ''}{estimatedPnL.toFixed(2)}$
                    </span>
                  </div>
                )}
              </div>
            )}

            <button onClick={handleTradeSubmit} disabled={loading || !tradePrice || !tradeQuantity} style={{ width: '100%', padding: '14px', backgroundColor: loading || !tradePrice || !tradeQuantity ? COLORS.border : tradeType === 'buy' ? COLORS.success : COLORS.danger, border: 'none', borderRadius: '8px', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
              {loading ? 'Обработка...' : tradeType === 'buy' ? `Купить ${tradeQuantity || 0} ${asset}` : `Продать ${tradeQuantity || 0} ${asset}`}
            </button>
          </div>
        )}

        {/* PnL Tab */}
        {activeTab === 'pnl' && (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Актив</label>
              <select 
                value={pnlAsset} 
                onChange={(e) => setPnlAsset(e.target.value)} 
                style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px' }}
              >
                {holdings.length > 0 ? holdings.map(h => {
                  const assetName = h.asset || h.symbol;
                  return <option key={assetName} value={assetName}>{assetName}</option>;
                }) : assets.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            {/* Current values for selected asset */}
            <div style={{ padding: '12px', backgroundColor: COLORS.background, borderRadius: '8px', marginBottom: '16px' }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '8px' }}>Текущие значения {pnlAsset}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Кол-во</div>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>{(pnlHolding.amount || pnlHolding.quantity || 0).toFixed(6)}</div>
                </div>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Ср. цена</div>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>${pnlHolding.avg_buy_price?.toFixed(2) || '0'}</div>
                </div>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Рыночная</div>
                  <div style={{ color: COLORS.primary, fontSize: '14px', fontWeight: '600' }}>${marketPrices[pnlAsset]?.toLocaleString() || '—'}</div>
                </div>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>PnL</div>
                  <div style={{ color: (pnlHolding.pnl || 0) >= 0 ? COLORS.success : COLORS.danger, fontSize: '14px', fontWeight: '600' }}>${pnlHolding.pnl?.toFixed(2) || '0'}</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Новый PnL ($)</label>
                <input type="number" value={newPnl} onChange={(e) => setNewPnl(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Ср. цена покупки ($)</label>
                  {marketPrices[pnlAsset] && (
                    <button
                      onClick={() => setNewAvgPrice(marketPrices[pnlAsset].toString())}
                      style={{ background: 'none', border: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: '10px' }}
                    >
                      Рын.
                    </button>
                  )}
                </div>
                <input type="number" value={newAvgPrice} onChange={(e) => setNewAvgPrice(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            </div>

            <button onClick={handlePnlSubmit} disabled={loading || (!newPnl && !newAvgPrice) || !pnlAsset} style={{ width: '100%', padding: '14px', backgroundColor: loading || (!newPnl && !newAvgPrice) || !pnlAsset ? COLORS.border : COLORS.primary, border: 'none', borderRadius: '8px', color: '#000', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
              {loading ? 'Сохранение...' : `Сохранить изменения ${pnlAsset}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpotBalanceModal;
